import { memo } from 'react';
import clsx from 'clsx';
import { ComponentMemo } from '@headless-workspace/typings';
import { DataTestConfig } from '@headless-workspace/config';
import {
    GlowButton,
    GlowImage,
    GlowRating,
    GlowSkeleton,
    GlowTag,
    GlowTextBodySmall,
    GlowTextCaption,
    GlowWishlistButton,
    IconComponentType,
    PropsWithDataLayer,
    PropsWithFontFamily,
    PropsWithImageTagName,
    PropsWithProduct,
} from '../../../../src';
import { GlowCTA } from '../cta/GlowCTA';
import { ProductPrices } from '../productPrices/ProductPrices';
import { ProductTileContentSkeleton, ProductTileImgSkeleton } from './ProductTileSkeleton';

type ProductTag = {
    id: string;
    label: string;
};

export type ProductTile = PropsWithImageTagName &
    PropsWithProduct &
    PropsWithDataLayer & {
        // These 2 attributes are not useful in the GlowProductTile component but are useful for the parent for the functional requirement
        sku: string;
        type: string;

        tags?: ProductTag[];
        minimalProductPriceLabel: string;
        minPrice?: string;
        basePriceLabel?: string;
        rating?: number;
        review?: {
            count: number;
            label: string;
        };
        noReviewsLabel: string;
        variantPossibilities?: string;
        engravingOptionLabel?: string;
        totalIndividualPriceLabel?: string;
        variantName?: string;
        button: {
            label: string;
            onClick?: (productId?: string) => void;
            icon?: IconComponentType;
            isLoading?: boolean;
        };
        legalMentionsLabel?: string;
    };

type GlowProductTileProps = ProductTile & {
    isTablet: boolean;
    isLoading?: boolean;
    wishlist: {
        onClick: () => void;
        isAdded: boolean;
        ariaLabel: string;
    };
    productTileId?: string;
};

const PRODUCT_TILE_IMG_SIZE_MOBILE = 188;
const PRODUCT_TILE_IMG_SIZE = 224;

export const GlowProductTile: ComponentMemo<GlowProductTileProps> = memo(
    ({
        id,
        name,
        description,
        href,
        image,
        brand,
        price,
        minPrice,
        priceBeforeDiscount,
        pricePerUnit,
        rating,
        review,
        tags,
        button,
        wishlist,
        isTablet,
        isArialFont,
        minimalProductPriceLabel,
        ImageTagName,
        priceAlwaysBlack,
        discountPercentageLabel,
        basePriceLabel,
        noReviewsLabel,
        isLoading = false,
        variantName,
        variantPossibilities,
        engravingOptionLabel,
        totalIndividualPriceLabel,
        legalMentionsLabel,
        dataLayer,
        productTileId,
    }) => {
        const fontFamily = isArialFont ? 'arial' : 'brand';

        const handleOpenMenu = (productId: string) => () => {
            button.onClick?.(productId);
        };

        return (
            <div
                data-product-id={id}
                className={clsx(
                    'relative flex flex-col justify-between gap-0.5',
                    'h-full w-productTileMobile tablet:w-productTile',
                    'p-0.5 pb-2.5',
                    'border-medium border-solid rounded-0.5 border-border-primary',
                    'bg-surface-primary',
                )}
            >
                <TagsAndWishlistButton tags={tags} wishlist={wishlist} isLoading={isLoading} />

                <GlowCTA
                    dataLayer={dataLayer}
                    className={clsx(
                        'flex flex-col justify-between gap-0.5 pt-0.25 h-full relative top-2',
                        dataLayer?.className,
                    )}
                    href={href}
                >
                    <div className={'flex flex-col gap-0.5'}>
                        {isLoading ? (
                            <ProductTileImgSkeleton />
                        ) : (
                            <GlowImage
                                image={image}
                                className={'mx-auto'}
                                width={isTablet ? PRODUCT_TILE_IMG_SIZE : PRODUCT_TILE_IMG_SIZE_MOBILE}
                                height={isTablet ? PRODUCT_TILE_IMG_SIZE : PRODUCT_TILE_IMG_SIZE_MOBILE}
                                loading={'lazy'}
                                TagName={ImageTagName}
                                imgClassName={'object-cover rounded-0.5'}
                            />
                        )}

                        <div 
                            className={'flex flex-col text-left gap-0.25'}
                            data-testid={DataTestConfig.testIds.productTileTitle}
                        >
                            <GlowTextBodySmall
                                className={'line-clamp-2'}
                                text={brand.name}
                                fontWeight={'bold'}
                                fontFamily={fontFamily}
                                TagName={'h3'}
                                id={productTileId} // used by aria-labelledby in GlowCarousel.tsx
                            />
                            <div className={'overflow-hidden h-productTileDetails'}>
                                <GlowTextBodySmall
                                    className={'line-clamp-2 text-ellipsis overflow-hidden whitespace-normal'}
                                    text={name}
                                    fontFamily={fontFamily}
                                />
                                <GlowTextBodySmall className={'truncate'} text={description} fontFamily={fontFamily} />
                            </div>
                            <GlowTextBodySmall
                                className={'line-clamp-1'}
                                text={variantName}
                                color={'tertiary'}
                                fontFamily={fontFamily}
                            />
                        </div>

                        {isLoading ? (
                            <ProductTileContentSkeleton />
                        ) : (
                            <>
                                <ProductPrices
                                    fontFamily={fontFamily}
                                    minPrice={minPrice}
                                    price={price}
                                    priceBeforeDiscount={priceBeforeDiscount}
                                    pricePerUnit={pricePerUnit}
                                    minimalProductPriceLabel={minimalProductPriceLabel}
                                    basePriceLabel={basePriceLabel}
                                    priceAlwaysBlack={priceAlwaysBlack}
                                    discountPercentageLabel={discountPercentageLabel}
                                    totalIndividualPriceLabel={totalIndividualPriceLabel}
                                    legalMentionsLabel={legalMentionsLabel}
                                />
                                <RatingAndReview
                                    rating={rating}
                                    review={review}
                                    noReviewsLabel={noReviewsLabel}
                                    fontFamily={fontFamily}
                                />
                                <div className={'flex flex-col'}>
                                    <GlowTextCaption
                                        className={'line-clamp-1'}
                                        text={variantPossibilities}
                                        color={'primary'}
                                        fontFamily={fontFamily}
                                    />

                                    <GlowTextCaption
                                        className={'line-clamp-1'}
                                        text={engravingOptionLabel}
                                        color={'primary'}
                                        fontFamily={fontFamily}
                                    />
                                    {legalMentionsLabel && (
                                        <GlowTextCaption
                                            className={'line-clamp-2'}
                                            text={legalMentionsLabel}
                                            color={'primary'}
                                            fontFamily={fontFamily}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </GlowCTA>

                {button.isLoading ? (
                    <GlowButton
                        label={button.label}
                        variant={'secondary'}
                        className={'relative top-2'}
                        fontFamily={fontFamily}
                        isSpinnerActive={true}
                        href={href}
                        dataTestId={`${DataTestConfig.testIds.productTileDiscoverButton}`}
                    />
                ) : button.onClick !== undefined ? (
                    <GlowButton
                        label={button.label}
                        variant={'secondary'}
                        className={'relative top-2'}
                        fontFamily={fontFamily}
                        onClick={handleOpenMenu(id)}
                        Icon={button.icon}
                        dataTestId={`${DataTestConfig.testIds.productTileDiscoverButton}`}
                    />
                ) : (
                    <GlowButton
                        label={button.label}
                        variant={'secondary'}
                        className={'relative top-2'}
                        fontFamily={fontFamily}
                        href={href}
                        dataTestId={`${DataTestConfig.testIds.productTileDiscoverButton}`}
                    />
                )}
            </div>
        );
    },
);

type TagsAndWishlistButtonProps = {
    tags?: ProductTag[];
    wishlist: {
        onClick: () => void;
        isAdded: boolean;
        ariaLabel: string;
    };
    isLoading: boolean;
};

const TagsAndWishlistButton: ComponentMemo<TagsAndWishlistButtonProps> = memo(({ tags, wishlist, isLoading }) => {
    return (
        <div
            className={clsx(
                'flex flex-row items-center',
                !isLoading && (tags && tags.length > 0 ? 'justify-between' : 'justify-end'),
                'absolute z-productTileTag w-productTileMobileWithoutPadding tablet:w-productTileWithoutPadding',
            )}
        >
            {isLoading ? (
                <GlowSkeleton width={'4rem'} height={'1.5rem'} />
            ) : (
                <>
                    {tags && (
                        <div className={'flex flex-col gap-0.25'}>
                            {tags.map((tag) => (
                                <GlowTag
                                    dataTestId={DataTestConfig.testIds.productTileBadge}
                                    key={tag.id}
                                    label={tag.label}
                                    color={'primaryBlack'}
                                    size={'medium'}
                                />
                            ))}
                        </div>
                    )}
                    <GlowWishlistButton
                        onClick={wishlist.onClick}
                        isInWishlist={wishlist.isAdded}
                        ariaLabel={wishlist.ariaLabel}
                        dataTestId={DataTestConfig.testIds.productTilewishlistButton}
                    />
                </>
            )}
        </div>
    );
});

type RatingAndReviewProps = PropsWithFontFamily & {
    rating?: number;
    review?: {
        count: number;
        label: string;
    };
    noReviewsLabel: string;
};

const RatingAndReview: ComponentMemo<RatingAndReviewProps> = memo(({ rating, review, noReviewsLabel, fontFamily }) => {
    if (rating && review) {
        return (
            <div
                className={'flex flex-row items-center gap-0.25'}
                data-testid={DataTestConfig.testIds.productTileRating}
            >
                <GlowRating rating={rating} className={'text-caption leading-4 gap-[0.188rem]'} />
                <GlowTextCaption text={`${review.count} ${review.label}`} color={'secondary'} fontFamily={fontFamily} />
            </div>
        );
    }

    return <GlowTextCaption text={noReviewsLabel} color={'secondary'} fontFamily={fontFamily} />;
});

GlowProductTile.displayName = 'GlowProductTile';
TagsAndWishlistButton.displayName = 'TagsAndWishlistButton';
RatingAndReview.displayName = 'RatingAndReview';
